import { apiService } from './api'
import { Transaction, TransactionFormData, PaginatedResponse, TransactionFilters } from '../types'

export class TransactionService {
  // Helper method to convert backend transaction format to frontend format
  private normalizeTransactionFromBackend(backendTransaction: any): Transaction {
    // Helper function to safely parse dates
    const safeDate = (dateValue: any, fieldName: string): string => {
      if (!dateValue || dateValue === null || dateValue === undefined) {
        console.warn(`Missing ${fieldName} date, using current date as fallback`)
        return new Date().toISOString().split('T')[0] // Default to today's date in YYYY-MM-DD format
      }
      
      // If it's already a valid date string, return it
      if (typeof dateValue === 'string') {
        // Handle common invalid date patterns from database
        if (dateValue === '0000-00-00' || dateValue === '0000-00-00 00:00:00' || dateValue.trim() === '') {
          console.warn(`Invalid ${fieldName} date from database: ${dateValue}, using current date as fallback`)
          return new Date().toISOString().split('T')[0]
        }
        
        // Try to parse the date to validate it
        const parsedDate = new Date(dateValue)
        if (!isNaN(parsedDate.getTime())) {
          // If it's a datetime string, extract just the date part
          if (dateValue.includes(' ') || dateValue.includes('T')) {
            return dateValue.split('T')[0].split(' ')[0]
          }
          // If it's a valid date, return the original string
          return dateValue
        }
      }
      
      // If parsing failed, return today's date as fallback
      console.warn(`Invalid ${fieldName} date value received from backend:`, dateValue, 'using current date as fallback')
      return new Date().toISOString().split('T')[0]
    }

    // Log the raw transaction data for debugging (only in development)
    if (import.meta.env.DEV) {
      console.log('Normalizing transaction:', {
        id: backendTransaction.id,
        transaction_date: backendTransaction.transaction_date || backendTransaction.transactionDate,
        created_at: backendTransaction.created_at || backendTransaction.createdAt,
        updated_at: backendTransaction.updated_at || backendTransaction.updatedAt
      })
    }

    return {
      id: backendTransaction.id,
      userId: backendTransaction.user_id || backendTransaction.userId,
      groupId: (backendTransaction.group_id || backendTransaction.groupId) || undefined,
      categoryId: backendTransaction.category_id || backendTransaction.categoryId,
      categoryName: backendTransaction.category_name || backendTransaction.categoryName,
      amount: backendTransaction.amount,
      description: backendTransaction.description,
      type: backendTransaction.type,
      date: safeDate(backendTransaction.transaction_date || backendTransaction.transactionDate, 'transaction_date'), // Keep for backward compatibility
      transactionDate: safeDate(backendTransaction.transaction_date || backendTransaction.transactionDate, 'transaction_date'),
      createdAt: safeDate(backendTransaction.created_at || backendTransaction.createdAt, 'created_at'),
      updatedAt: safeDate(backendTransaction.updated_at || backendTransaction.updatedAt, 'updated_at'),
      category: backendTransaction.category,
      tags: backendTransaction.tags || [],
      group: backendTransaction.group,
      userName: backendTransaction.user_name || backendTransaction.userName
    }
  }
  async getTransactions(
    page = 1,
    limit = 50,
    filters?: TransactionFilters
  ): Promise<PaginatedResponse<Transaction>> {
    // Convert camelCase filters to snake_case for backend API
    const backendFilters: any = {}
    if (filters?.type) backendFilters.type = filters.type
    if (filters?.categoryId) backendFilters.category_id = filters.categoryId
    if (filters?.tagIds) backendFilters.tag_ids = filters.tagIds
    if (filters?.dateFrom) backendFilters.date_from = filters.dateFrom
    if (filters?.dateTo) backendFilters.date_to = filters.dateTo
    if (filters?.groupId) backendFilters.group_id = filters.groupId
    if (filters?.search) backendFilters.search = filters.search
    
    const params = {
      page,
      limit,
      ...backendFilters,
    }
    
    try {
      const response = await apiService.getRaw<{
        transactions: any[]
        pagination: any
      }>('/transactions', params)
      
      console.log('Get transactions response:', {
        success: response.data.success,
        transactionCount: response.data.data?.transactions?.length || 0
      })
      
      // Check if response has expected structure
      if (!response.data.success || !response.data.data || !response.data.data.transactions) {
        console.error('Unexpected API response structure:', response.data)
        throw new Error('Invalid API response structure')
      }
      
      const responseData = response.data.data
      
      // Convert backend response to frontend format
      return {
        data: responseData.transactions.map(this.normalizeTransactionFromBackend.bind(this)),
        pagination: {
          page: responseData.pagination.current_page || page,
          limit: responseData.pagination.limit || limit,
          total: responseData.pagination.total || 0,
          totalPages: responseData.pagination.pages || 0
        }
      }
    } catch (error) {
      console.error('Error fetching transactions:', error)
      throw error
    }
  }

  async getTransaction(id: number): Promise<Transaction> {
    const response = await apiService.getRaw<{ transaction: any }>(`/transactions/${id}`)
    
    if (!response.data.success || !response.data.data || !response.data.data.transaction) {
      throw new Error('Invalid API response structure')
    }
    
    return this.normalizeTransactionFromBackend(response.data.data.transaction)
  }

  async createTransaction(data: TransactionFormData): Promise<Transaction> {
    // Convert camelCase to snake_case for backend API
    const backendData = {
      amount: data.amount,
      description: data.description,
      type: data.type,
      category_id: data.categoryId,
      transaction_date: data.transactionDate,
      tag_ids: data.tagIds,
      ...(data.groupId && { group_id: data.groupId })
    }
    
    console.log('Creating transaction with data:', backendData)
    
    try {
      const response = await apiService.postRaw<{ transaction: any }>('/transactions', backendData)
      
      console.log('Create transaction response:', {
        success: response.data.success,
        hasTransaction: !!response.data.data?.transaction
      })
      
      if (!response.data.success || !response.data.data || !response.data.data.transaction) {
        console.error('Invalid create transaction API response structure:', response.data)
        throw new Error('Invalid API response structure')
      }
      
      return this.normalizeTransactionFromBackend(response.data.data.transaction)
    } catch (error) {
      console.error('Error creating transaction:', error)
      throw error
    }
  }

  async updateTransaction(id: number, data: Partial<TransactionFormData>): Promise<Transaction> {
    // Convert camelCase to snake_case for backend API
    const backendData: any = {}
    
    if (data.amount !== undefined) backendData.amount = data.amount
    if (data.description !== undefined) backendData.description = data.description
    if (data.type !== undefined) backendData.type = data.type
    if (data.categoryId !== undefined) backendData.category_id = data.categoryId
    if (data.transactionDate !== undefined) backendData.transaction_date = data.transactionDate
    if (data.tagIds !== undefined) backendData.tag_ids = data.tagIds
    if (data.groupId !== undefined) backendData.group_id = data.groupId
    
    const response = await apiService.putRaw<{ transaction: any }>(`/transactions/${id}`, backendData)
    
    if (!response.data.success || !response.data.data || !response.data.data.transaction) {
      throw new Error('Invalid API response structure')
    }
    
    return this.normalizeTransactionFromBackend(response.data.data.transaction)
  }

  async deleteTransaction(id: number): Promise<void> {
    await apiService.deleteRaw(`/transactions/${id}`)
  }

  async getRecentTransactions(limit = 10): Promise<Transaction[]> {
    try {
      const response = await apiService.getRaw<{ transactions: any[] }>('/transactions/recent', { limit })
      
      if (!response.data.success || !response.data.data || !response.data.data.transactions) {
        console.error('Unexpected recent transactions API response structure:', response.data)
        return []
      }
      
      return response.data.data.transactions.map(this.normalizeTransactionFromBackend.bind(this))
    } catch (error) {
      console.error('Error fetching recent transactions:', error)
      throw error
    }
  }

  async getTransactionSummary(
    startDate?: string,
    endDate?: string
  ): Promise<{
    totalIncome: number
    totalExpenses: number
    netAmount: number
    transactionCount: number
  }> {
    const params = { start_date: startDate, end_date: endDate }
    const response = await apiService.getRaw<{ summary: any }>('/transactions/summary', params)
    
    if (!response.data.success || !response.data.data || !response.data.data.summary) {
      throw new Error('Invalid API response structure')
    }
    
    return response.data.data.summary
  }

  async searchTransactions(query: string, filters?: TransactionFilters): Promise<Transaction[]> {
    // Convert camelCase filters to snake_case for backend API
    const backendFilters: any = {}
    if (filters?.type) backendFilters.type = filters.type
    if (filters?.categoryId) backendFilters.category_id = filters.categoryId
    if (filters?.tagIds) backendFilters.tag_ids = filters.tagIds
    if (filters?.dateFrom) backendFilters.date_from = filters.dateFrom
    if (filters?.dateTo) backendFilters.date_to = filters.dateTo
    if (filters?.groupId) backendFilters.group_id = filters.groupId
    
    const params = { q: query, ...backendFilters }
    
    try {
      const response = await apiService.getRaw<{ transactions: any[] }>('/transactions/search', params)
      
      if (!response.data.success || !response.data.data || !response.data.data.transactions) {
        console.error('Unexpected search API response structure:', response.data)
        return []
      }
      
      return response.data.data.transactions.map(this.normalizeTransactionFromBackend.bind(this))
    } catch (error) {
      console.error('Error searching transactions:', error)
      throw error
    }
  }

  async getPersonalTransactions(
    page = 1,
    limit = 50,
    filters?: TransactionFilters
  ): Promise<PaginatedResponse<Transaction>> {
    // Convert camelCase filters to snake_case for backend API
    const backendFilters: any = {}
    if (filters?.type) backendFilters.type = filters.type
    if (filters?.categoryId) backendFilters.category_id = filters.categoryId
    if (filters?.tagIds) backendFilters.tag_ids = filters.tagIds
    if (filters?.dateFrom) backendFilters.date_from = filters.dateFrom
    if (filters?.dateTo) backendFilters.date_to = filters.dateTo
    if (filters?.groupId) backendFilters.group_id = filters.groupId
    if (filters?.search) backendFilters.search = filters.search
    
    const params = { page, limit, ...backendFilters }
    
    try {
      const response = await apiService.getRaw<{
        transactions: any[]
        pagination: any
      }>('/transactions/personal', params)
      
      if (!response.data.success || !response.data.data || !response.data.data.transactions) {
        console.error('Unexpected personal transactions API response structure:', response.data)
        throw new Error('Invalid API response structure')
      }
      
      const responseData = response.data.data
      
      return {
        data: responseData.transactions.map(this.normalizeTransactionFromBackend.bind(this)),
        pagination: {
          page: responseData.pagination.current_page || page,
          limit: responseData.pagination.limit || limit,
          total: responseData.pagination.total || 0,
          totalPages: responseData.pagination.pages || 0
        }
      }
    } catch (error) {
      console.error('Error fetching personal transactions:', error)
      throw error
    }
  }

  async getGroupTransactions(
    page = 1,
    limit = 50,
    filters?: TransactionFilters
  ): Promise<PaginatedResponse<Transaction>> {
    // Convert camelCase filters to snake_case for backend API
    const backendFilters: any = {}
    if (filters?.type) backendFilters.type = filters.type
    if (filters?.categoryId) backendFilters.category_id = filters.categoryId
    if (filters?.tagIds) backendFilters.tag_ids = filters.tagIds
    if (filters?.dateFrom) backendFilters.date_from = filters.dateFrom
    if (filters?.dateTo) backendFilters.date_to = filters.dateTo
    if (filters?.groupId) backendFilters.group_id = filters.groupId
    if (filters?.search) backendFilters.search = filters.search
    
    const params = { page, limit, ...backendFilters }
    
    try {
      const response = await apiService.getRaw<{
        transactions: any[]
        pagination: any
        group_id?: number
      }>('/transactions/groups', params)
      
      if (!response.data.success || !response.data.data || !response.data.data.transactions) {
        console.error('Unexpected group transactions API response structure:', response.data)
        throw new Error('Invalid API response structure')
      }
      
      const responseData = response.data.data
      
      return {
        data: responseData.transactions.map(this.normalizeTransactionFromBackend.bind(this)),
        pagination: {
          page: responseData.pagination.current_page || page,
          limit: responseData.pagination.limit || limit,
          total: responseData.pagination.total || 0,
          totalPages: responseData.pagination.pages || 0
        }
      }
    } catch (error) {
      console.error('Error fetching group transactions:', error)
      throw error
    }
  }

  async bulkCreateTransactions(transactions: TransactionFormData[]): Promise<{
    created: Transaction[]
    errors: Array<{ index: number; error: string; data: TransactionFormData }>
    successCount: number
    errorCount: number
  }> {
    // Convert all transactions to backend format
    const backendTransactions = transactions.map(data => ({
      amount: data.amount,
      description: data.description,
      type: data.type,
      category_id: data.categoryId,
      transaction_date: data.transactionDate,
      tag_ids: data.tagIds,
      ...(data.groupId && { group_id: data.groupId })
    }))
    
    try {
      const response = await apiService.postRaw<{
        created: any[]
        errors: Array<{ index: number; error: string; data: any }>
        successCount: number
        errorCount: number
      }>('/transactions/bulk', { transactions: backendTransactions })
      
      if (!response.data.success || !response.data.data) {
        console.error('Unexpected bulk create API response structure:', response.data)
        throw new Error('Invalid API response structure')
      }
      
      const responseData = response.data.data
      
      return {
        created: (responseData.created || []).map(this.normalizeTransactionFromBackend.bind(this)),
        errors: (responseData.errors || []).map(error => ({
          ...error,
          data: transactions[error.index] // Return original frontend format
        })),
        successCount: responseData.successCount || 0,
        errorCount: responseData.errorCount || 0
      }
    } catch (error) {
      console.error('Error bulk creating transactions:', error)
      throw error
    }
  }
}

export const transactionService = new TransactionService()